/**
 * 固件 mock 数据与版本比较（对齐 111/firmwareMockData.js）
 */

import type { FirmwareMockItem, FirmwareTab } from './firmware-types'
import { FIRMWARE_TABS, VERSION_KEY_MAP } from './firmware-types'

const MOCK_FIRMWARE_DATA: Record<FirmwareTab, FirmwareMockItem> = {
  earphone: {
    deviceName: 'Headset Pro',
    latestVersion: 'V1.1.2',
    size: '796 KB',
    description: 'Optimized audio EQ for richer bass and clearer treble.',
    historyList: [
      { version: 'V1.1.2', description: 'Optimized audio EQ.', update_date: '2026.04.15 10:20' },
      { version: 'V1.1.1', description: 'Fixed static noise.', update_date: '2026.03.10 09:15' },
    ],
  },
  earphoneBluetooth: {
    deviceName: 'Headset Pro (BT)',
    latestVersion: 'V1.1.2',
    size: '777 KB',
    description: 'Enhanced Bluetooth connection stability.',
    historyList: [
      { version: 'V1.1.2', description: 'Enhanced Bluetooth stability.', update_date: '2026.04.20 14:00' },
    ],
  },
  earphoneNoiseCancel: {
    deviceName: 'Headset Pro (ENC)',
    latestVersion: 'V1.1.2',
    size: '453 KB',
    description: 'Upgraded wind-noise reduction algorithm.',
    historyList: [
      { version: 'V1.1.2', description: 'Advanced noise cancellation.', update_date: '2026.04.22 16:40' },
    ],
  },
  dongle: {
    deviceName: 'Headset Pro (Dongle)',
    latestVersion: 'V1.1.2',
    size: '496 KB',
    description: 'Resolved USB 3.0 compatibility issues.',
    historyList: [
      { version: 'V1.1.2', description: 'Fixed stability issues.', update_date: '2026.04.23 09:00' },
    ],
  },
}

export function parseFirmwareVersion(version: string): number[] {
  if (!version) return []
  return String(version)
    .trim()
    .replace(/^[vV]/, '')
    .split('.')
    .map(n => parseInt(n, 10) || 0)
}

export function compareFirmwareVersion(v1: string, v2: string): number {
  const a = parseFirmwareVersion(v1)
  const b = parseFirmwareVersion(v2)
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i++) {
    const n1 = a[i] || 0
    const n2 = b[i] || 0
    if (n1 > n2) return 1
    if (n1 < n2) return -1
  }
  return 0
}

export function getFirmwareMockData(tab: FirmwareTab): FirmwareMockItem {
  return MOCK_FIRMWARE_DATA[tab] || MOCK_FIRMWARE_DATA.earphone
}

export function getFirmwareHistoryList(tab: FirmwareTab) {
  const data = getFirmwareMockData(tab)
  return Array.isArray(data.historyList) ? data.historyList : []
}

export function getDeviceCurrentVersion(
  tab: FirmwareTab,
  deviceDetail?: Record<string, unknown> | null
): string {
  const key = VERSION_KEY_MAP[tab]
  const version = deviceDetail?.[key]
  return typeof version === 'string' && version.trim() !== '' ? version.trim() : ''
}

export function hasFirmwareUpdate(tab: FirmwareTab, deviceDetail?: Record<string, unknown> | null): boolean {
  const current = getDeviceCurrentVersion(tab, deviceDetail)
  const latest = (getFirmwareMockData(tab).latestVersion || '').trim()
  return !!(current && latest && compareFirmwareVersion(latest, current) > 0)
}

export function getTabsWithFirmwareUpdate(deviceDetail?: Record<string, unknown> | null): FirmwareTab[] {
  return FIRMWARE_TABS.filter(tab => hasFirmwareUpdate(tab, deviceDetail))
}

export function hasAnyFirmwareUpdate(deviceDetail?: Record<string, unknown> | null): boolean {
  return getTabsWithFirmwareUpdate(deviceDetail).length > 0
}

export function buildFirmwareUrl(tab: FirmwareTab, version: string): string {
  const v = String(version || '').trim()
  const folder = tab ? (tab === 'earphone' ? 'Earphone' : tab === 'earphoneBluetooth' ? 'EarphoneBluetooth' : tab === 'earphoneNoiseCancel' ? 'EarphoneNoiseCancel' : 'Dongle') : ''
  if (!folder || !v) return ''
  const base = import.meta.env.BASE_URL || '/'
  return `${base}upgrading/${folder}/${v}.up`
}
